/**
 * Splits an already-ordered category list into the primary list and the ones
 * an admin flagged to live under the synthetic "Other" menu.
 *
 * Centralized so the navbar and the Free Trial selector (and any future
 * consumer) share one definition of the split instead of each re-deriving it.
 * The input order is preserved in both output arrays — the backend already
 * sorts by `position`, so callers must not re-sort.
 *
 * Generic over any shape carrying `showInOther`, because the two consumers use
 * different category types (the web `LearningSectionItem` vs the booking
 * `BookingCategoryOption`).
 */
export function partitionByOther<T extends { showInOther?: boolean }>(
  items: T[],
): { primary: T[]; other: T[] } {
  const primary: T[] = [];
  const other: T[] = [];

  for (const item of items) {
    if (item.showInOther) {
      other.push(item);
    } else {
      primary.push(item);
    }
  }

  return { primary, other };
}

/**
 * Resolves the Region-aware top-level category set: categories with no
 * Parent Category, plus any child whose parent didn't survive upstream
 * filtering (wrong region/Global, inactive, show_in_navbar off, etc.) -
 * promoted to stand in for it for this Region only. The DB hierarchy is
 * untouched; this only decides what counts as "top level" for display.
 *
 * This is the single canonical source for that decision - reused by the
 * Navbar (`resolveNavbarGroups`), the student catalog tabs, and the homepage
 * /categories sections grid - so a parent-less-in-this-region child is
 * promoted consistently everywhere instead of silently disappearing.
 */
export function resolveTopLevelCategories<T extends { id: number; parentId: number | null }>(
  items: T[],
): T[] {
  const visibleIds = new Set(items.map((item) => item.id));

  return items.filter((item) => item.parentId === null || !visibleIds.has(item.parentId));
}

/**
 * Groups categories by the Parent Category they're assigned to, so the
 * Navbar can render each top-level category's subs. Categories with no
 * parentId are omitted (they're top-level themselves, see `resolveTopLevelCategories`).
 */
export function groupChildrenByParent<T extends { parentId: number | null }>(
  items: T[],
): Map<number, T[]> {
  const map = new Map<number, T[]>();

  for (const item of items) {
    if (item.parentId === null) {
      continue;
    }

    const siblings = map.get(item.parentId);
    if (siblings) {
      siblings.push(item);
    } else {
      map.set(item.parentId, [item]);
    }
  }

  return map;
}

/**
 * Builds the Navbar's category groups. A child nests under its parent only
 * when that parent is itself present in `items` (i.e. currently visible for
 * this region/navbar); a child whose parent didn't survive filtering (wrong
 * region, inactive, or Show in Navbar off) is promoted to a standalone entry
 * instead, split by its own `showInOther` - a child's navbar presence never
 * depends on its parent's.
 */
export function resolveNavbarGroups<
  T extends { id: number; parentId: number | null; showInOther?: boolean },
>(items: T[]): { primary: T[]; other: T[]; childrenByParentId: Map<number, T[]> } {
  const childrenByParentId = groupChildrenByParent(items);
  const { primary, other } = partitionByOther(resolveTopLevelCategories(items));

  return { primary, other, childrenByParentId };
}
